import { Box, Tooltip, Typography } from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { useRef } from "react";

import type { StoryMapSummary } from "../../types/storyMap";
import { storyThumbnailUrl } from "./storyRows";

interface RootStoriesCardProps {
  /** Newest first; the first is the cover shown. */
  stories: StoryMapSummary[];
  highlighted: boolean;
  onHover: (hovering: boolean) => void;
  onOpenStory: (story: StoryMapSummary) => void;
  onOpenStack: (anchor: HTMLElement) => void;
}

/**
 * Organisation-wide story maps have no spot on the map (their extent is every
 * project, whose centre is usually open water), so they sit in a card pinned
 * to the map's corner, drawn like the per-project thumbnails.
 */
export default function RootStoriesCard({ stories, highlighted, onHover, onOpenStory, onOpenStack }: RootStoriesCardProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  if (stories.length === 0) return null;
  const thumb = storyThumbnailUrl(stories[0], 144, 92);
  const stacked = stories.length > 1;
  const card = (offset: number, opacity: number, front = false) => (
    <Box
      key={offset}
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: "6px",
        border: `2px solid ${front && highlighted ? "#00d4ad" : "#fff"}`,
        backgroundColor: "#cbd5e1",
        backgroundImage: front && thumb ? `url("${thumb}")` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.45)",
        transform: `translate(${offset}px, ${-offset}px)`,
        opacity,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94a3b8",
      }}
    >
      {front && !thumb && <MapOutlinedIcon fontSize="small" />}
    </Box>
  );
  return (
    <Tooltip title={stacked ? `${stories.length} story maps across every project` : stories[0].title} placement="left">
      <Box
        ref={ref}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
        onClick={() => (stacked ? onOpenStack(ref.current as HTMLElement) : onOpenStory(stories[0]))}
        sx={{
          position: "absolute",
          top: 56,
          right: 18,
          zIndex: 20,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <Box sx={{ position: "relative", width: 72, height: 46 }}>
          {stories.length > 2 && card(8, 0.7)}
          {stacked && card(4, 0.85)}
          {card(0, 1, true)}
          {stacked && (
            <Box
              sx={{
                position: "absolute",
                right: -8,
                bottom: -8,
                minWidth: 20,
                height: 20,
                px: 0.6,
                borderRadius: 10,
                backgroundColor: "#00d4ad",
                color: "#0b1119",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: "20px",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.4)",
              }}
            >
              {stories.length}
            </Box>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            backgroundColor: "rgba(30, 41, 59, 0.9)",
            color: "#f8fafc",
            px: 0.75,
            borderRadius: 1,
            fontWeight: 600,
          }}
        >
          All projects
        </Typography>
      </Box>
    </Tooltip>
  );
}
